import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'

import { createInstance, getInstance, type ModuleFederation } from '@module-federation/runtime'

// Module Federation runtime bootstrap for Client Portal host.
// Same pattern as docs/MODULE_FEDERATION_GUIDE.md — do not use @module-federation/vite.
//
// Why we expose `instance.loadRemote` (and NOT the top-level `loadRemote`):
//   Top-level `loadRemote` can throw RUNTIME-009 under HMR / timing. Binding
//   `$loadRemote` to the cached instance method avoids that.

let mfInstance: ModuleFederation | null = null

const ensureMfInstance = (manifestUrl: string): ModuleFederation => {
  const existing = mfInstance ?? getInstance()
  if (existing) {
    mfInstance = existing
    return existing
  }

  mfInstance = createInstance({
    name: 'clientPortalApp',
    remotes: [
      {
        name: 'membershipPreviewApp',
        entry: manifestUrl,
        shareScope: 'default',
      },
    ],
    shared: {},
  })

  if (import.meta.dev) {
    console.info('[MF] Federation runtime created for host "clientPortalApp" with manifest:', manifestUrl)
  }

  return mfInstance
}

const loadConfiguredRemote = async <T = unknown>(manifestUrl: string, id: string): Promise<T> => {
  const remoteModule = await ensureMfInstance(manifestUrl).loadRemote<T>(id)
  if (!remoteModule) {
    throw new Error(`[MF] Remote module "${id}" resolved to null.`)
  }

  return remoteModule
}

export default defineNuxtPlugin(() => {
  const { membershipPreviewMfManifestUrl } = useRuntimeConfig().public as {
    membershipPreviewMfManifestUrl?: string
  }

  if (typeof window !== 'undefined' && membershipPreviewMfManifestUrl) {
    ensureMfInstance(membershipPreviewMfManifestUrl)
  }

  return {
    provide: {
      loadRemote: <T = unknown>(id: string): Promise<T> => {
        if (!membershipPreviewMfManifestUrl) {
          return Promise.reject(
            new Error(
              '[MF] membershipPreviewMfManifestUrl is not configured. ' +
                'Set MEMBERSHIP_PREVIEW_REMOTE_URL or fix runtimeConfig.public.membershipPreviewMfManifestUrl in nuxt.config.ts.'
            )
          )
        }
        return loadConfiguredRemote<T>(membershipPreviewMfManifestUrl, id)
      },
    },
  }
})
